import {
  Button,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PlaylistAddRounded, UpdateRounded } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  createDisciplineAsync,
  getDisciplineAsync,
  updateDisciplineAsync
} from './disciplineSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    },
    columnFullWidth: {
      width: '100%',
      marginBottom: theme.spacing(2)
    }
  })
);

interface CreateEditDiscipline {
  id: number | null;
  name: string | null;
  cfihosName: string | null;
}

export default function DisciplineCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedDiscipline } = useSelector(
    (state: RootState) => state.disciplines
  );

  const params = useParams<{ disciplineId: string }>();
  const disciplineId = Number.parseInt(params.disciplineId, 10);
  const isNewPage = !Boolean(disciplineId);

  const [discipline, setDiscipline] = useState<CreateEditDiscipline>({
    id: null,
    name: null,
    cfihosName: null
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedDiscipline) {
      setDiscipline({
        ...discipline,
        id: selectedDiscipline.id,
        name: selectedDiscipline.name,
        cfihosName: selectedDiscipline.cfihosName
      });
    }
  }, [selectedDiscipline]);

  useEffect(() => {
    if (!isNewPage) dispatch(getDisciplineAsync(disciplineId));
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setDiscipline({
      ...discipline,
      [name]: value === '' ? null : value
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (discipline.name && discipline.cfihosName) {
        if (isNewPage) {
          // create discipline
          const createDiscipline = {
            name: discipline.name,
            cfihosName: discipline.cfihosName
          };
          await dispatch(createDisciplineAsync(createDiscipline)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('masterData.discipline.messages.createdSuccessfully')
            })
          );
        } else {
          // update discipline
          const updateDiscipline = {
            id: discipline.id,
            name: discipline.name,
            cfihosName: discipline.cfihosName
          };
          await dispatch(updateDisciplineAsync(updateDiscipline)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('masterData.discipline.messages.updated')
            })
          );
        }
      } else {
        throw new Error('There was an error. Please try again');
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('masterData.discipline.title'),
          masterDataRef: 'disciplines',
          masterDataSubName: selectedDiscipline?.name,
          masterDataSubRef: selectedDiscipline?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage
          ? t('masterData.discipline.newTitle')
          : t('masterData.discipline.editTitle')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('masterData.discipline.info')}
          </Typography>
          <Divider className={classes.divider} />
          <TextField
            className={classes.columnFullWidth}
            id="discipline-name"
            label={t('masterData.discipline.fields.name')}
            size="small"
            variant="outlined"
            name="name"
            fullWidth
            value={discipline.name ? discipline.name : ''}
            onChange={onChangeField}
            required
          />
          <TextField
            id="cfihos-name"
            label={t('masterData.discipline.fields.cfihosName')}
            size="small"
            variant="outlined"
            name="cfihosName"
            fullWidth
            value={discipline.cfihosName ? discipline.cfihosName : ''}
            onChange={onChangeField}
            required
          />
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage ? t('masterData.discipline.add') : t('projectInfoCard.update')}
        </Button>
      </form>
    </div>
  );
}
